import uuid from "uuid";
import { Record } from "immutable";

import { StateError } from "@orm/models";
import { ApiOptions } from "../models/actions";

type RequestState = "CREATED" | "IN_PROGRESS" | "SUCCEEDED" | "FAILED";

interface IApiRequest {
    uuid?: string;
    options?: ApiOptions;
    state?: RequestState;
    started?: Date;
    finished?: Date;
    error?: StateError;
    response?: any;
    progress?: number;
}

const ApiRequestRecord = Record({
    uuid: null,
    options: null,
    state: "CREATED",
    started: null,
    finished: null,
    error: null,
    response: null,
    progress: null,
});

interface QueryParams {
    [name: string]: string;
}

export class ApiRequest extends ApiRequestRecord implements IApiRequest {
    public uuid: string;
    public options: ApiOptions;
    public state: RequestState;
    public started: Date;
    public finished: Date;
    public error: StateError;
    public response: any;
    public progress: number;
    // Other parameters
    public queryParams: QueryParams;

    /**
     * Initialises a created request with a uuid.
     *
     * @param IApiRequest props
     */
    constructor(props: IApiRequest) {
        if (props.uuid == null) {
            props.uuid = uuid.v4();
        }
        super(props);
    }

    /**
     * Function to get the endpoint.
     *
     * @return string
     */
    public getEndpoint(): string {
        if (this.options != null && this.options.endpoint != null) {
            return this.options.endpoint;
        }
        return null;
    }

    /**
     * Function to get the uri.
     *
     * @return string
     */
    public getUri(): string {
        if (this.options != null && this.options.uri != null) {
            return this.options.uri;
        }
        return null;
    }

    /**
     * Function to get the query parameter for the given name.
     *
     * @param string name
     * @return string
     */
    public query(name: string): string {
        // Initlise queryParams
        if (this.queryParams == null) {
            this.queryParams = getQueryParams(this.options.endpoint);
        }

        if (this.queryParams[name] != null) {
            return this.queryParams[name];
        }
        return null;
    }

    /**
     * Function to get the ID from an endpoint.
     *
     * @return number
     */
    public getId(): number {
        const idStr = this.getIdStr();
        if (idStr != null) {
            const id = Number(idStr);
            return isNaN(id) ? null : id;
        }
        return null;
    }

    /**
     * Function to get the ID from an endpoint as string.
     *
     * @return number
     */
    public getIdStr(): string {
        const endpoint = this.getEndpoint();
        if (endpoint != null) {
            const uri = this.getUri();
            let idx = -1;
            if (uri != null && uri.includes("{id}")) {
                let regex = uri.replace("/", "\/");
                regex = uri.replace("{id}", "([^/]+)");
                const matches = endpoint.match(regex);
                if (matches != null) {
                    return matches[1];
                }
            }
            // If it doesn't match, the uri, just send the trailing part of the url
            idx = endpoint.lastIndexOf("/");
            if (idx > -1) {
                return endpoint.substr(idx + 1);
            }
        }
        return null;
    }

    /**
     * Set the progress of the request.
     *
     * @param number progress
     * @return ApiRequest
     */
    public setProgress(progress: number): ApiRequest {
        return this.merge({ progress }) as this;
    }

    /**
     * Function to get a copy of the ApiRequest, setting in progress.
     *
     * @return ApiRequest
     */
    public setInProgress(): ApiRequest {
        return this.merge({
            state: "IN_PROGRESS",
            started: new Date(),
        }) as this;
    }

    /**
     * Function to get a copy of the ApiRequest, setting finished - succeeded.
     *
     * @return ApiRequest
     */
    public setFinishedSucceeded(response: any = null): ApiRequest {
        return this.merge({
            state: "SUCCEEDED",
            finished: new Date(),
            response,
        }) as this;
    }

    /**
     * Function to get a copy of the ApiRequest, setting finished - failed.
     *
     * @param StateError error
     * @return ApiRequest
     */
    public setFinishedFailed(error: StateError): ApiRequest {
        const obj = this.merge({
            state: "FAILED",
            finished: new Date(),
        }) as this;
        return obj.set("error", error) as this;
    }

    /**
     * Check if the request is in progress
     *
     * @return boolean
     */
    public isInProgress(): boolean {
        return this.state === "IN_PROGRESS";
    }

    /**
     * Check if the request is finished - succeeded
     *
     * @return boolean
     */
    public isFinished(): boolean {
        return this.state === "SUCCEEDED" || this.state === "FAILED";
    }

    /**
     * Check if the request is finished - succeeded
     *
     * @return boolean
     */
    public isFinishedSucceeded(): boolean {
        return this.state === "SUCCEEDED";
    }

    /**
     * Check if the request is finished - succeeded
     *
     * @return boolean
     */
    public isFinishedFailed(): boolean {
        return this.state === "FAILED";
    }

    /**
     * Check whether the request finished in the last 10 seconds.
     *
     * @return boolean
     */
    public hasFinishedRecently(): boolean {
        const now = new Date();
        return this.isFinished() && this.finished != null
            && ((now.getTime() - this.finished.getTime()) / 1000) <= 10;
    }
}

export const getQueryParams = (query: string) => {
    if (query != null && query !== "") {
        // Strip off the part before the query.
        const queryIdx = query.indexOf("?");
        if (queryIdx > -1) {
            query = query.substr(queryIdx + 1);
        }
        // Now split into an object
        return query.split("&").reduce(
            (params, param) => {
                const [ key, value ] = param.split("=");
                if (key !== "") {
                    params[key] = value ? decodeURIComponent(value.replace(/\+/g, " ")) : "";
                }
                return params;
            }, {} as QueryParams);
    }
    return {};
};
